// @ts-strict-ignore
import React, {
  useEffect,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import isEqual from 'lodash/isEqual';

import type { InputSection, GapOptions, SizeOptions } from './types';
import FormModalBody from './FormModalBody';

interface FormModalProps {
  header?: string;
  inputSections: Array<InputSection>;
  isOpen: boolean;
  onSubmit: (payload) => void;
  onToggleModal: () => void;
  primaryButtonLabel: string;
  secondaryButtonLabel: string;
  sectionGap?: GapOptions;
  size?: SizeOptions;
  maxHeight?: number;
  defaultValues?: { [name: string]: any };
  disableSubmitButtonInvalid?: boolean;
}

/**
 * @deprecated Please use the Modal component from the Component Library
 */
const FormModal = ({
  size,
  header,
  inputSections,
  isOpen,
  onSubmit,
  onToggleModal,
  primaryButtonLabel,
  secondaryButtonLabel,
  sectionGap,
  defaultValues,
  disableSubmitButtonInvalid,
}: FormModalProps) => {
  // Initialization for custom components
  // customInputsInitialValue is based on the customDefaultValue, and is used to check if custom components are dirty
  // customRequiredValidInitial and customValidationFunctions are used to validate values for required custom components
  const {
    customInputsInitialValue,
    customRequiredValidInitial,
    customValidationFunctions,
  } = inputSections.reduce(
    (acc, {
      customDefaultValue,
      customKey,
      customRequiresValidation,
      customValidationFunction,
    }) => {
      if (customKey) {
        acc.customInputsInitialValue[customKey] = customDefaultValue === undefined
          ? null
          : customDefaultValue;
        if (customRequiresValidation && customKey) {
          acc.customRequiredValidInitial[customKey] = false;
          acc.customValidationFunctions[customKey] = customValidationFunction;
        }
      }
      return acc;
    }, {
      customInputsInitialValue: {},
      customRequiredValidInitial: {},
      customValidationFunctions: {},
    },
  );
  const formMethods = useForm({ defaultValues, mode: 'all' });
  const [customInputs, setCustomInputs] = useState(customInputsInitialValue);
  const [isCustomDirty, setIsCustomDirty] = useState(false);
  const [customRequiredValid, setCustomRequiredValid] = useState(customRequiredValidInitial);

  useEffect(() => {
    formMethods.reset(defaultValues);
  }, [formMethods, defaultValues]);

  useEffect(() => {
    if (isEqual(customInputs, customInputsInitialValue)) {
      setIsCustomDirty(false);
    } else {
      setIsCustomDirty(true);
    }
  }, [customInputs, customInputsInitialValue]);

  useEffect(() => {
    const newCustomRequriedValid = Object.entries(customInputs).reduce((acc, [key, value]) => {
      if (customValidationFunctions[key]) {
        const validationFunction = customValidationFunctions[key];
        const isValid = validationFunction(value);
        return { ...acc, [key]: isValid };
      }
      return acc;
    }, {});
    setCustomRequiredValid(newCustomRequriedValid);
  }, [customInputs]);

  const onClose = () => {
    onToggleModal();
    formMethods.reset();
  };

  const onSubmitClose = formData => {
    const payload = { ...formData, ...customInputs };
    onSubmit(payload);
    onClose();
  };

  return (
    <FormProvider {...formMethods}>
      <FormModalBody
        isCustomDirty={isCustomDirty}
        size={size}
        header={header}
        inputSections={inputSections}
        isOpen={isOpen}
        onSubmit={onSubmitClose}
        onClose={onClose}
        primaryButtonLabel={primaryButtonLabel}
        secondaryButtonLabel={secondaryButtonLabel}
        setCustomInputs={setCustomInputs}
        customInputs={customInputs}
        sectionGap={sectionGap}
        customRequiredValid={customRequiredValid}
        disableSubmitButtonInvalid={disableSubmitButtonInvalid}
      />
    </FormProvider>
  );
};

export default FormModal;
